<template>
  <div class="bg-gray-100 py-10 overflow-y-scroll w-full h-full scroll-styling">
    <div class="container mx-auto px-4">
      <h1 class="text-4xl font-extrabold text-black text-center mb-6 bg-clip-text">
        Онлайн Академия
      </h1>

      <h2 class="text-3xl font-bold text-gray-800 mb-8 border-b-2 border-blue-500 inline-block">Обучение внешних пилотов на БПЛА</h2>

      <div class="bg-white rounded-2xl shadow-lg p-6 text-gray-700 leading-relaxed">
        <p class="mb-6">
          Мы предлагаем программы обучения внешних пилотов на беспилотные авиационные системы (БАС), ориентированные как на начинающих, так и на профессиональных пользователей.
          Обучение строится с учётом современных требований и реальных условий эксплуатации БПЛА.
        </p>

        <h3 class="text-2xl font-semibold mb-4">Курсы для начинающих</h3>
        <p class="mb-4">
          Краткосрочная программа, включающая теоретическую часть и практическое освоение навыков управления квадрокоптерами.
          Обучение подходит для слушателей с минимальным уровнем подготовки. Возрастная категория — от 9 лет.
        </p>

        <h3 class="text-2xl font-semibold mb-4">Курсы для профессионалов</h3>
        <p class="mb-4">
          Продвинутая программа подготовки внешних пилотов в соответствии с действующими нормами и правилами эксплуатации БПЛА.
          Курс охватывает вопросы законодательства, планирования полётов, обеспечения безопасности и эффективного управления воздушным пространством.
        </p>
        <ul class="list-disc list-inside mb-4 text-gray-700">
          <li>Основы национального и международного авиационного законодательства</li>
          <li>Планирование полётов и подача заявок</li>
          <li>Практические навыки управления БПЛА в режимах VLOS и RLOS</li>
          <li>Развитие аналитического мышления и навыков предотвращения рисков</li>
        </ul>
        <p class="mb-6 font-bold text-gray-800">
          Продолжительность и стоимость обучения определяются в зависимости от выбранной программы
        </p>

        <h3 class="text-2xl font-semibold mb-4">Требования к кандидатам</h3>
        <p class="mb-4">
          Пройти обучение могут совершеннолетние граждане, имеющие среднее образование и соответствующие медицинские показатели.
          Подтверждение отсутствия ограничений по здоровью требуется в установленной форме.
        </p>

        <h3 class="text-2xl font-semibold mb-4">Организация обучения</h3>
        <p class="mb-4">
          Обучение включает в себя лекционные занятия, самостоятельную работу, практические тренировки и итоговую аттестацию.
          По завершении курса выдается сертификат о прохождении подготовки внешнего пилота БПЛА.
        </p>

        <h3 class="text-2xl font-semibold mb-4">Преимущества обучения</h3>
        <ul class="list-disc list-inside text-gray-700">
          <li>Очный формат обучения с доступом к квалифицированным преподавателям</li>
          <li>Практическая подготовка с использованием современных БПЛА</li>
          <li>Гибкая система обучения и доступная стоимость программ</li>
        </ul>
      </div>

      <section v-if="courses.length > 0" class="container mx-auto px-4 mb-16 mt-10">
        <h2 class="text-3xl font-bold text-gray-800 mb-8 border-b-2 border-emerald-500 inline-block">Доступные курсы</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 bg-white rounded-2xl shadow-lg p-6 text-gray-700 leading-relaxed">
          <div
            v-for="(course, ind) in courses"
            :key="'course-' + ind"
            class="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-emerald-300 cursor-pointer flex flex-col h-full">

            <div class="relative h-48 md:h-56 overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300">
              <div
                :style="{
                  backgroundImage: course.image
                    ? `url(${course.image})`
                    : 'url(/assets/images/download.jpeg)',
                }"
                class="w-full h-full bg-cover bg-center group-hover:scale-105 transition-transform duration-300">
              </div>
              <div v-if="course.price > 0" class="absolute top-3 right-3 bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                {{ course.price }} ₸
              </div>
            </div>

            <div class="p-5 flex flex-col flex-grow">
              <h3 class="text-lg font-bold text-gray-800 mb-3 line-clamp-2 group-hover:text-emerald-600 transition-colors">
                {{ course.title }}
              </h3>

              <div v-if="course.teacher" class="flex items-center gap-2 mb-3 text-sm text-gray-600">
                <span class="text-emerald-500 font-semibold">👨‍🏫</span>
                <span>{{ course.teacher }}</span>
              </div>

              <div class="space-y-2 text-sm flex-grow">
                <div class="flex items-center justify-between text-gray-700">
                  <span class="text-gray-500">Форма обучения:</span>
                  <span class="font-semibold text-gray-800">
                    {{ course.course_type.val }}
                  </span>
                </div>

                <div class="flex items-center justify-between text-gray-700">
                  <span class="text-gray-500">Мест доступно:</span>
                  <span class="font-semibold text-gray-800">
                    {{ course.max_amount }}
                  </span>
                </div>

                <div v-if="course.hours" class="flex items-center justify-between text-gray-700">
                  <span class="text-gray-500">Часов:</span>
                  <span class="font-semibold text-gray-800">
                    {{ course.hours }} ч
                  </span>
                </div>
              </div>
            </div>

            <div class="w-full px-5 pb-5 flex flex-col gap-2">
              <router-link
                v-slot="{ href, navigate }"
                :to="{
                  name: 'CourseDetail',
                  params: {
                    courseId: course.uuid,
                  },
                }"
                class="w-full">
                <a
                  :href="href"
                  @click="navigate"
                  class="block w-full bg-gray-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-600 transition-all duration-200 shadow-md hover:shadow-lg text-center text-sm uppercase tracking-wide">
                  Подробнее
                </a>
              </router-link>

              <div v-if="course.status" class="px-4 py-3 rounded-lg text-center font-bold text-sm uppercase tracking-wide border-2"
                :class="{
                  'bg-green-50 border-green-500 text-green-700': course.status === 'Одобрил',
                  'bg-yellow-50 border-yellow-500 text-yellow-700': course.status === 'Не рассмотрена',
                  'bg-red-50 border-red-500 text-red-700': course.status === 'Отклонил',
                  'bg-blue-50 border-blue-500 text-blue-700': course.status && !['Одобрил', 'Не рассмотрена', 'Отклонил'].includes(course.status),
                }">
                Статус: {{ course.status }}
              </div>

            </div>
          </div>
        </div>
      </section>

      <section v-if="typeof user.key != 'undefined'">
        <h2 class="text-3xl font-bold text-gray-800 mb-8 border-b-2 border-blue-500 inline-block">Эксперты</h2>
        <div class="grid gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          <div
            v-for="expert in experts"
            :key="'expert-' + expert.id"
            class="bg-white rounded-2xl shadow-lg hover:shadow-2xl hover:scale-105 transition transform duration-300 overflow-hidden flex flex-col items-center text-center p-6"
          >
            <img
              v-if="expert.photo"
              :src="expert.photo"
              alt="Фото эксперта"
              class="h-32 w-32 object-cover rounded-full mb-4"
            />
            <h3 class="text-lg font-semibold mb-1">{{ expert.fio }}</h3>
            <p class="text-gray-600 mb-2">{{ expert.specialization }}</p>
            <p class="text-sm text-gray-500">Рейтинг: {{ expert.rating.toFixed(1) }} ({{ expert.rating_count }} оценок)</p>
          </div>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex'
import { API_COURSES, API_CLIENT, HEADERS_TOKEN_JSON } from '@/const/uri';
import { getSelectOptionsBase } from '@/utils/base';

export default {
  data() {
    return {
      courses: [],
      experts: [],
      isAuthenticated: false,
    };
  },
  computed: {
    ...mapState(['user']),
  },
  mounted() {
    this.checkAuthentication();
    this.loadData();
  },
  methods: {
    checkAuthentication() {
      const token = localStorage.getItem('user_token');
      this.isAuthenticated = !!token;
    },
    openApplicationModal(courseUUID) {
      // Redirect to /client/courses with courseUUID query parameter
      this.$router.push({
        path: '/client/courses',
        query: { courseUUID }
      });
    },
    async loadData() {
      await Promise.all([
        this.fetchCoursesData(),
      ]);
    },
    async fetchCourses() {
      try {
        const response = await axios.get(API_COURSES.list, { });
        if (response.data.code === 0) {
          this.courses = response.data.data.results;
        }
      } catch (error) {
        console.error('Ошибка загрузки курсов:', error);
      }
    },
    getSelectOptionsBase,
    formatDate(date) {
      const options = {
        day: "numeric",
        month: "long",
        year: "numeric",
      };
      const formatter = new Intl.DateTimeFormat("ru-RU", options);
      return formatter.format(date);
    },
    async fetchCoursesData() {
      await axios({
        method: "GET",
        params: {},
        headers: HEADERS_TOKEN_JSON(),
        url: API_CLIENT.courses,
      })
      .then((e) => {
        this.courses = [];
        e.data.data.forEach(el => {
          this.courses.push({
            ...el,
            date_started: new Date(el.date_started),
          });
        });
      })
      .catch((e) => {
        console.log(e);
      });
    },
  },
};
</script>
